import { useState, useEffect } from 'react';
import API from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { S } from '../../components/pageStyles';
import { Loader, StatCard } from '../../components/ui';
import { LuBan, LuPackage, LuStore, LuTriangleAlert, LuWallet } from 'react-icons/lu';

import { fmtF } from '../../theme';
const SECTEURS = {
  boissons: { label: 'Boissons', couleur: '#EA580C', bg: '#FFF7ED' },
  restaurant: { label: 'Restaurant', couleur: '#0E7490', bg: '#ECFEFF' },
  vetements: { label: 'Vêtements', couleur: '#7C3AED', bg: '#F5F3FF' },
  cosmetiques: { label: 'Cosmétiques', couleur: '#D81B60', bg: '#FCE4EC' },
  gaz: { label: 'Gaz Butane', couleur: '#E65100', bg: '#FBE9E7' },
  general: { label: 'Général', couleur: '#455A64', bg: '#ECEFF1' },
};

export default function StocksProprietaire() {
  const [etablissements, setEtablissements] = useState([]);
  const [etabSelectionne, setEtabSelectionne] = useState(null);
  const [produits, setProduits] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('tous');

  useEffect(() => { chargerDonnees(); }, []);

  const chargerDonnees = async () => {
    try {
      const [etabRes, alertesRes] = await Promise.all([
        API.get('/etablissements'),
        API.get('/rapports/stock')
      ]);
      const etabs = etabRes.data.etablissements || [];
      setEtablissements(etabs);
      setAlertes(alertesRes.data.produits || []);
      if (etabs.length > 0) {
        await chargerProduits(etabs[0].id);
        setEtabSelectionne(etabs[0].id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

  const chargerProduits = async (etabId) => {
    try {
      const res = await API.get('/produits?etablissement_id=' + etabId);
      setProduits(res.data.produits || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectEtab = (etabId) => {
    setEtabSelectionne(etabId);
    chargerProduits(etabId);
  };

  const produitsFiltres = produits.filter(p => {
    const matchRecherche = p.nom.toLowerCase().includes(recherche.toLowerCase());
    const matchStatut =
      filtreStatut === 'tous' ? true :
      filtreStatut === 'rupture' ? p.quantite === 0 :
      filtreStatut === 'alerte' ? p.quantite > 0 && p.quantite <= p.seuil_alerte :
      filtreStatut === 'normal' ? p.quantite > p.seuil_alerte : true;
    return matchRecherche && matchStatut;
  });

  const etabActuel = etablissements.find(e => e.id === etabSelectionne);
  const alertesEtab = alertes.filter(a => a.etablissement_id === etabSelectionne);
  const valeurStock = produits.reduce((t, p) =>
    t + parseFloat(p.prix || 0) * parseInt(p.quantite || 0), 0
  );

  if (chargement) return <Loader />;

  return (
    <AppLayout role="proprietaire" badges={{ '/proprietaire/stocks': alertes.length }}>

      {/* CONTENU */}
      <div style={styles.contenu}>

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitre}>Gestion des Stocks</h1>
            <p style={styles.headerSousTitre}>
              {etablissements.length + ' établissement(s) • ' +
               alertes.length + ' alerte(s) au total'}
            </p>
          </div>
        </div>

        {/* SÉLECTEUR ÉTABLISSEMENT */}
        <div style={styles.etabSelector}>
          <div style={styles.etabSelectorTitre}>Sélectionner un établissement</div>
          <div style={styles.etabSelectorListe}>
            {etablissements.map((etab, i) => {
              const secteur = SECTEURS[etab.secteur_stock] || SECTEURS.general;
              const alertesEtabCount = alertes.filter(a => a.etablissement_id === etab.id).length;
              return (
                <div key={i} onClick={() => handleSelectEtab(etab.id)} style={{
                  ...styles.etabCard,
                  border: etabSelectionne === etab.id
                    ? '2px solid #EA580C' : '2px solid #E2E8F0',
                  background: etabSelectionne === etab.id ? '#FFF7ED' : 'white',
                  cursor: 'pointer'
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: secteur.bg, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: 20, color: secteur.couleur, flexShrink: 0
                  }}>
                    <LuStore aria-hidden />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0F172A' }}>
                      {etab.nom}
                    </div>
                    <div style={{ color: '#64748B', fontSize: 11 }}>{secteur.label}</div>
                    {alertesEtabCount > 0 && (
                      <div style={{ color: '#DC2626', fontSize: 11, fontWeight: 700 }}>
                        <LuTriangleAlert aria-hidden style={{ verticalAlign: '-0.125em' }} /> {alertesEtabCount} alerte(s)
                      </div>
                    )}
                  </div>
                  {etabSelectionne === etab.id && (
                    <span style={{ color: '#EA580C', fontSize: 18 }}>✓</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* STATS ÉTABLISSEMENT SÉLECTIONNÉ */}
        {etabActuel && (
          <>
            <div style={styles.statsGrid}>
              {[
                { icon: <LuPackage aria-hidden />, label: 'Total produits', valeur: produits.length, couleur: '#2563EB', bg: '#EFF6FF' },
                { icon: <LuWallet aria-hidden />, label: 'Valeur stock', valeur: fmtF(valeurStock), couleur: '#059669', bg: '#ECFDF5' },
                { icon: <LuBan aria-hidden />, label: 'Ruptures', valeur: produits.filter(p => p.quantite === 0).length, couleur: '#DC2626', bg: '#FEF2F2' },
                { icon: <LuTriangleAlert aria-hidden />, label: 'Alertes', valeur: alertesEtab.length, couleur: '#EA580C', bg: '#FFF7ED' },
              ].map((stat, i) => (
                <StatCard key={i} icon={stat.icon} label={stat.label} value={stat.valeur} color={stat.couleur} />
              ))}
            </div>

            {/* FILTRES */}
            <div style={styles.filtres}>
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                style={styles.rechercheInput}
              />
              <div style={styles.filtreBtns}>
                {[
                  { val: 'tous', label: 'Tous', count: produits.length },
                  { val: 'normal', label: 'Normal', count: produits.filter(p => p.quantite > p.seuil_alerte).length },
                  { val: 'alerte', label: 'Alerte', count: alertesEtab.length },
                  { val: 'rupture', label: 'Rupture', count: produits.filter(p => p.quantite === 0).length },
                ].map(f => (
                  <button key={f.val} onClick={() => setFiltreStatut(f.val)} style={{
                    padding: '8px 14px', borderRadius: 8, border: 'none',
                    cursor: 'pointer', fontWeight: 700, fontSize: 12,
                    background: filtreStatut === f.val ? 'var(--accent)' : '#F1F5F9',
                    color: filtreStatut === f.val ? 'white' : '#475569'
                  }}>
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>
            </div>

            {/* TABLEAU PRODUITS */}
            <div style={styles.section}>
              <h2 style={styles.sectionTitre}>
                {'Stock de ' + etabActuel.nom + ' — ' + produitsFiltres.length + ' produit(s)'}
              </h2>
              {produitsFiltres.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>
                  <div style={{ fontSize: 48 }}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                  <p>Aucun produit trouvé</p>
                </div>
              ) : (
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      {['Produit', 'Prix unitaire', 'Quantité', 'Seuil alerte', 'Valeur stock', 'Statut'].map((h, i) => (
                        <th key={i} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {produitsFiltres.map((p, i) => {
                      const statut = p.quantite === 0 ? 'rupture'
                        : p.quantite <= p.seuil_alerte ? 'alerte' : 'normal';
                      const statutConfig = {
                        rupture: { label: 'Rupture', couleur: '#DC2626', bg: '#FEF2F2' },
                        alerte: { label: 'Alerte', couleur: '#EA580C', bg: '#FFF7ED' },
                        normal: { label: 'Normal', couleur: '#059669', bg: '#ECFDF5' },
                      };
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#F8FAFC' : 'white' }}>
                          <td style={styles.td}>
                            <div style={{ fontWeight: 700 }}>{p.nom}</div>
                            <div style={{ color: '#64748B', fontSize: 11 }}>{p.unite}</div>
                          </td>
                          <td style={styles.td}>
                            {fmtF(parseInt(p.prix))}
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              fontWeight: 700, fontSize: 18,
                              color: statut === 'rupture' ? '#DC2626'
                                : statut === 'alerte' ? '#EA580C' : '#059669'
                            }}>
                              {p.quantite}
                            </span>
                            <span style={{ color: '#64748B', fontSize: 11 }}>
                              {' ' + p.unite}
                            </span>
                          </td>
                          <td style={styles.td}>{p.seuil_alerte}</td>
                          <td style={styles.td}>
                            <span style={{ fontWeight: 700, color: '#2563EB' }}>
                              {fmtF((parseInt(p.prix) * parseInt(p.quantite)))}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <span style={{
                              padding: '4px 10px', borderRadius: 12,
                              fontSize: 12, fontWeight: 700,
                              background: statutConfig[statut].bg,
                              color: statutConfig[statut].couleur
                            }}>
                              {statutConfig[statut].label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

const styles = {
  chargement: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 24, color: '#475569' },
  contenu: { ...S.contenu, flex: 1 },
  header: S.header,
  headerTitre: S.headerTitre,
  headerSousTitre: S.headerSousTitre,
  etabSelector: { ...S.etabSelector, marginBottom: 20 },
  etabSelectorTitre: { ...S.etabSelectorTitre, marginBottom: 12 },
  etabSelectorListe: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  etabCard: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 16px', borderRadius: 10, minWidth: 180, maxWidth: 220
  },
  statsGrid: { ...S.statsGrid, marginBottom: 20 },
  statCard: S.statCard,
  filtres: { ...S.filtres, marginBottom: 16 },
  rechercheInput: { ...S.rechercheInput },
  filtreBtns: { display: 'flex', gap: 8 },
  section: S.section,
  sectionTitre: { ...S.sectionTitre, marginBottom: 16 },
  table: { ...S.table, width: '100%' },
  tableHeader: S.tableHeader,
  th: S.th,
  td: S.td,
};